import React, { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import '../../assets/css/general/privacyPolicy.css';

const PrivacyPolicy = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const handleGoBack = () => {
        if (location.state?.returnTo) {
            navigate(location.state.returnTo, { state: location.state.returnState });
        } else {
            navigate(-1);
        }
    };

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const sections = [
        {
            id: 'overview',
            icon: 'fas fa-eye',
            title: '1. Overview',
            content: `Northern Pakistan Gallery ("we", "our", or "us") is committed to protecting your personal data. This Privacy Policy explains how we collect, use, store, and share information from Customers, Vendors, and any visitors to our platform. By using our services, you agree to the practices described in this policy.`
        },
        {
            id: 'data-collected',
            icon: 'fas fa-database',
            title: '2. Information We Collect',
            content: `We collect different types of information depending on your role:`,
            subsections: [
                {
                    subtitle: 'For Customers',
                    list: [
                        'Full name, phone number, and email address',
                        'City/location for booking purposes',
                        'Check-in / check-out dates and number of guests',
                        'Payment transaction references',
                        'Support ticket content and communication history',
                        'AI Planner inputs (budget and preferences)'
                    ]
                },
                {
                    subtitle: 'For Vendors',
                    list: [
                        'Business/hotel name and location',
                        'Contact details and CNIC/business registration information',
                        'Bank account details for payment settlements',
                        'Room listings, photos, and pricing information'
                    ]
                },
                {
                    subtitle: 'Automatically Collected',
                    list: [
                        'Browser type, IP address, and device information',
                        'Pages visited, time spent, and click behavior',
                        'Cookie identifiers and session data'
                    ]
                }
            ]
        },
        {
            id: 'data-use',
            icon: 'fas fa-cogs',
            title: '3. How We Use Your Information',
            content: null,
            list: [
                { text: 'To process and confirm hotel and package bookings.', bold: false },
                { text: 'To communicate booking confirmations, updates, and support responses.', bold: false },
                { text: 'To enable Vendors to manage their hotel properties and rooms.', bold: false },
                { text: 'To power the AI Travel Planner with your budget and preference inputs.', bold: false },
                { text: 'To detect and prevent fraudulent transactions or misuse.', bold: false },
                { text: 'To improve platform performance and user experience.', bold: false },
                { text: 'To send service-related notifications (not marketing, unless you opt in).', bold: false }
            ]
        },
        {
            id: 'data-sharing',
            icon: 'fas fa-share-alt',
            title: '4. Data Sharing',
            content: `We do not sell your personal data. We may share information only in the following limited cases:`,
            list: [
                { text: 'With Vendors: Customer booking details are shared with the relevant hotel Vendor to fulfill the reservation.', bold: true },
                { text: 'With Payment Processors: Transaction data is shared with our secure payment gateway partners.', bold: true },
                { text: 'With Admin: Platform administrators can access data to manage operations and resolve disputes.', bold: true },
                { text: 'Legal Compliance: We may disclose data when required by law or to protect our rights.', bold: true },
                { text: 'Service Providers: Trusted third-party tools (e.g., analytics, cloud hosting) under strict data agreements.', bold: true }
            ]
        },
        {
            id: 'cookies',
            icon: 'fas fa-cookie-bite',
            title: '5. Cookies & Tracking',
            content: `We use cookies to enhance your experience on our platform:`,
            list: [
                { text: 'Essential Cookies: Required for core platform functionality (login sessions, booking state).', bold: true },
                { text: 'Analytics Cookies: Help us understand how users navigate the platform.', bold: true },
                { text: 'Preference Cookies: Remember your settings and language preferences.', bold: true },
                { text: 'You can disable cookies in your browser settings, but some features may not work correctly.', bold: false }
            ]
        },
        {
            id: 'ai-planner',
            icon: 'fas fa-robot',
            title: '6. AI Travel Planner & Data',
            content: `When you use our AI Planner feature, your budget and travel preferences are sent to an AI model to generate itinerary suggestions. Please note:`,
            list: [
                { text: 'AI Planner inputs are used solely to generate your travel suggestions.', bold: false },
                { text: 'We do not store or reuse your AI Planner prompts for marketing purposes.', bold: false },
                { text: 'AI-generated itineraries are not binding contracts or confirmed bookings.', bold: false },
                { text: 'Do not enter sensitive personal or financial details into the AI Planner.', bold: false }
            ]
        },
        {
            id: 'data-retention',
            icon: 'fas fa-archive',
            title: '7. Data Retention',
            content: null,
            list: [
                { text: 'Booking data is retained for 3 years for financial and legal compliance.', bold: false },
                { text: 'Account data is retained until you request deletion of your account.', bold: false },
                { text: 'Support ticket history is kept for 1 year after resolution.', bold: false },
                { text: 'Analytics data is aggregated and anonymized after 6 months.', bold: false }
            ]
        },
        {
            id: 'your-rights',
            icon: 'fas fa-user-shield',
            title: '8. Your Rights',
            content: `As a user of our platform, you have the following rights regarding your personal data:`,
            list: [
                { text: 'Access: Request a copy of the personal data we hold about you.', bold: true },
                { text: 'Correction: Request that inaccurate or incomplete data be corrected.', bold: true },
                { text: 'Deletion: Request deletion of your account and associated data.', bold: true },
                { text: 'Portability: Request your data in a portable, machine-readable format.', bold: true },
                { text: 'Objection: Object to specific uses of your data (e.g., analytics).', bold: true },
                { text: 'To exercise any of these rights, contact us at privacy@northern.pk.', bold: false }
            ]
        },
        {
            id: 'security',
            icon: 'fas fa-lock',
            title: '9. Data Security',
            content: `We take the security of your data seriously and implement the following measures:`,
            list: [
                { text: 'All data is transmitted via HTTPS/TLS encryption.', bold: false },
                { text: 'Passwords are hashed and never stored in plain text.', bold: false },
                { text: 'Payment data is handled via PCI-DSS compliant processors.', bold: false },
                { text: 'Access to user data is restricted to authorized personnel only.', bold: false },
                { text: 'Regular security audits and vulnerability assessments are conducted.', bold: false }
            ]
        },
        {
            id: 'contact',
            icon: 'fas fa-envelope',
            title: '10. Contact & DPO',
            content: `For privacy-related questions, data requests, or to report a concern, contact us:`,
            list: [
                { text: 'Privacy Email: privacy@northern.pk', bold: true },
                { text: 'General Support: support@northern.pk', bold: true },
                { text: 'Phone: +92 300 123 4567', bold: true },
                { text: 'Office: Gilgit, Northern Pakistan', bold: true },
                { text: 'Hours: 9:00 AM – 6:00 PM (PKT), Monday – Saturday', bold: true }
            ]
        }
    ];

    const renderListItems = (items) => {
        return items.map((item, index) => {
            if (item.bold) {
                return <li key={index}><strong>{item.text}</strong></li>;
            }
            return <li key={index}>{item.text}</li>;
        });
    };

    return (
        <div className="pp-page-wrapper">
            {/* Hero Section */}
            <div className="pp-hero">
                <div className="pp-hero-overlay"></div>
                <div className="pp-hero-content">
                    <div className="pp-hero-badge">
                        <i className="fas fa-shield-alt me-2"></i>Data Protection
                    </div>
                    <h1 className="pp-hero-title">Privacy Policy</h1>
                    <p className="pp-hero-subtitle">
                        How we collect, use, and protect your personal information
                    </p>
                </div>
            </div>

            {/* Content */}
            <div className="pp-content-wrapper">
                <div className="pp-container">

                    {/* Intro Banner */}
                    <div className="pp-intro-banner">
                        <i className="fas fa-shield-alt pp-intro-icon"></i>
                        <p>
                            Your privacy matters to us. This policy applies to all users of the Northern Pakistan Gallery — 
                            including <strong>Customers</strong> booking hotels and packages, <strong>Vendors</strong> managing 
                            properties, and <strong>visitors</strong> browsing our content.
                        </p>
                    </div>

                    {/* Table of Contents */}
                    <div className="pp-toc-card">
                        <h3 className="pp-toc-title">
                            <i className="fas fa-list-ul me-2"></i>Table of Contents
                        </h3>
                        <div className="pp-toc-grid">
                            {sections.map((section) => (
                                <a
                                    key={section.id}
                                    href={`#${section.id}`}
                                    className="pp-toc-item"
                                >
                                    <i className={section.icon}></i>
                                    <span>{section.title}</span>
                                </a>
                            ))}
                        </div>
                    </div>

                    {/* Sections */}
                    <div className="pp-sections">
                        {sections.map((section) => (
                            <div key={section.id} id={section.id} className="pp-section-card">
                                <div className="pp-section-header">
                                    <div className="pp-section-icon">
                                        <i className={section.icon}></i>
                                    </div>
                                    <h2 className="pp-section-title">{section.title}</h2>
                                </div>
                                <div className="pp-section-body">
                                    {section.content && (
                                        <p className="pp-section-text">{section.content}</p>
                                    )}
                                    {section.list && (
                                        <ul className="pp-section-list">
                                            {renderListItems(section.list)}
                                        </ul>
                                    )}
                                    {section.subsections && section.subsections.map((sub, si) => (
                                        <div key={si} className="pp-subsection">
                                            <h4 className="pp-subsection-title">
                                                <i className="fas fa-angle-right me-2"></i>{sub.subtitle}
                                            </h4>
                                            <ul className="pp-section-list">
                                                {sub.list.map((item, i) => (
                                                    <li key={i}>{item}</li>
                                                ))}
                                            </ul>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Footer Actions */}
                    <div className="pp-footer-actions">
                        <button className="pp-back-btn" onClick={handleGoBack}>
                            <i className="fas fa-arrow-left me-2"></i> Go Back
                        </button>
                        <button className="pp-top-btn" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
                            <i className="fas fa-arrow-up me-2"></i> Back to Top
                        </button>
                    </div>

                </div>
            </div>
        </div>
    );
};

export default PrivacyPolicy;